import type { CrudFilters } from "@refinedev/core";

/** Tristate for the enabled toolbar select. */
export type EnabledFilter = "all" | "enabled" | "disabled";

/**
 * A source selection is either a *group* ("web"/"instagram"/"facebook", mapped
 * to the backend `source_kind` param) or a specific web strategy ("api"/"simple"/
 * "sitemap", mapped to exact `x_strategy`). This predicate distinguishes them.
 */
export function isSourceGroup(source: string): boolean {
  return source === "web" || source === "instagram" || source === "facebook";
}

/**
 * Toolbar state for the server-side sites list. Empty strings / "all" mean
 * "no filter on this field" — they are omitted from the resulting CrudFilters.
 */
export interface SiteFilterState {
  /** Partial-match search against `name`. */
  name?: string;
  /** Exact match against `content_type` ("" = all). */
  contentType?: string;
  /**
   * Source selection ("" = all). Groups "web"/"instagram"/"facebook" map to
   * `source_kind`; a specific strategy "api"/"simple"/"sitemap" maps to exact
   * `x_strategy`.
   */
  source?: string;
  /** Exact match against `country_code` ("" = all). */
  countryCode?: string;
  /** Partial match against `city` ("" = all). */
  city?: string;
  /** Tristate boolean for `enabled`. */
  enabled?: EnabledFilter;
  /** Tristate for the "needs manual unblock" toolbar select. */
  needsManualUnblock?: "all" | "yes" | "no";
}

/**
 * Pure mapping from toolbar state to the `eq`-operator CrudFilters the tina
 * data provider forwards as flat `?field=value` query params. Only set values
 * are included; "all"/empty values are omitted.
 */
export function buildSiteFilters(state: SiteFilterState): CrudFilters {
  const filters: CrudFilters = [];

  const name = state.name?.trim();
  if (name) filters.push({ field: "name", operator: "eq", value: name });

  if (state.contentType) {
    filters.push({
      field: "content_type",
      operator: "eq",
      value: state.contentType,
    });
  }

  if (state.source) {
    filters.push(
      isSourceGroup(state.source)
        ? { field: "source_kind", operator: "eq", value: state.source }
        : { field: "x_strategy", operator: "eq", value: state.source },
    );
  }

  if (state.countryCode) {
    filters.push({
      field: "country_code",
      operator: "eq",
      value: state.countryCode,
    });
  }

  const city = state.city?.trim();
  if (city) filters.push({ field: "city", operator: "eq", value: city });

  if (state.enabled === "enabled") {
    filters.push({ field: "enabled", operator: "eq", value: true });
  } else if (state.enabled === "disabled") {
    filters.push({ field: "enabled", operator: "eq", value: false });
  }

  if (state.needsManualUnblock === "yes") {
    filters.push({ field: "needs_manual_unblock", operator: "eq", value: true });
  } else if (state.needsManualUnblock === "no") {
    filters.push({
      field: "needs_manual_unblock",
      operator: "eq",
      value: false,
    });
  }

  return filters;
}
